/**
 * ProductCard3D - CSS-based 3D animated product card
 * Shows sale prices, discounts, free shipping, SKU, and variants
 */
import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import api from "../lib/api";
import { Star, Heart, Eye, Plus, Check, Store, Truck, Tag, Package, Layers } from "lucide-react";

const THEMES = {
  green: { glow: "rgba(0, 155, 58, 0.4)", accent: "#009B3A", badge: "bg-[#009B3A]" },
  gold: { glow: "rgba(252, 209, 22, 0.4)", accent: "#FCD116", badge: "bg-[#FCD116] text-black" },
  black: { glow: "rgba(100, 100, 100, 0.4)", accent: "#333333", badge: "bg-[#333333]" },
  red: { glow: "rgba(255, 59, 48, 0.4)", accent: "#FF3B30", badge: "bg-[#FF3B30]" },
  orange: { glow: "rgba(255, 149, 0, 0.4)", accent: "#FF9500", badge: "bg-[#FF9500] text-black" },
  yellow: { glow: "rgba(255, 204, 0, 0.4)", accent: "#FFCC00", badge: "bg-[#FFCC00] text-black" },
  lime: { glow: "rgba(52, 199, 89, 0.4)", accent: "#34C759", badge: "bg-[#34C759]" },
  teal: { glow: "rgba(90, 200, 250, 0.4)", accent: "#5AC8FA", badge: "bg-[#5AC8FA] text-black" },
  cyan: { glow: "rgba(50, 173, 188, 0.4)", accent: "#32ADBC", badge: "bg-[#32ADBC]" },
  blue: { glow: "rgba(0, 122, 255, 0.4)", accent: "#007AFF", badge: "bg-[#007AFF]" },
  purple: { glow: "rgba(175, 82, 222, 0.4)", accent: "#AF52DE", badge: "bg-[#AF52DE]" },
  pink: { glow: "rgba(255, 45, 85, 0.4)", accent: "#FF2D55", badge: "bg-[#FF2D55]" },
  coral: { glow: "rgba(255, 111, 97, 0.4)", accent: "#FF6F61", badge: "bg-[#FF6F61]" },
  mint: { glow: "rgba(0, 199, 190, 0.4)", accent: "#00C7BE", badge: "bg-[#00C7BE]" },
  white: { glow: "rgba(255, 255, 255, 0.3)", accent: "#666666", badge: "bg-gray-200 text-black" },
  classic: { glow: "rgba(255, 255, 255, 0.3)", accent: "#ffffff", badge: "bg-gray-800" },
  neon: { glow: "rgba(0, 168, 107, 0.6)", accent: "#00A86B", badge: "bg-gradient-to-r from-[#00A86B] to-[#FFD300]" }
};

export default function ProductCard3D({ 
  product, 
  theme = "green",
  onAddToCart,
  showMerchant = true,
  size = "default"
}) {
  const navigate = useNavigate();
  const cardRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [addedSuccess, setAddedSuccess] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isLiked, setIsLiked] = useState(false);

  const themeColors = THEMES[theme] || THEMES.green;

  const originalPrice = product.original_price || product.compare_at_price;
  const isOnSale = product.on_sale || (originalPrice && originalPrice > product.price);
  const discountPercent = isOnSale && originalPrice 
    ? Math.round(((originalPrice - product.price) / originalPrice) * 100)
    : null;
  const hasVariants = product.variants && product.variants.length > 0;

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setMousePosition({ x, y });
  };

  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => {
    setIsHovered(false);
    setMousePosition({ x: 0.5, y: 0.5 });
  };

  const handleAddToCart = async (e) => {
    e.stopPropagation();
    if (isAdding) return;
    setIsAdding(true);
    try {
      if (onAddToCart) {
        await onAddToCart(product.id);
      } else {
        await api.post("/cart/add", { product_id: product.id, quantity: 1 });
        toast.success("Added to cart!");
      }
      setAddedSuccess(true);
      setTimeout(() => setAddedSuccess(false), 2000);
    } catch (error) {
      toast.error("Failed to add to cart");
    } finally {
      setIsAdding(false);
    }
  };

  const handleLike = (e) => {
    e.stopPropagation();
    setIsLiked(!isLiked);
    toast.success(isLiked ? "Removed from wishlist" : "Added to wishlist");
  };

  const rotateX = isHovered ? (mousePosition.y - 0.5) * -15 : 0;
  const rotateY = isHovered ? (mousePosition.x - 0.5) * 15 : 0;
  const translateZ = isHovered ? 20 : 0;

  const cardStyles = {
    transform: `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateZ(${translateZ}px)`,
    transition: isHovered ? "transform 0.1s ease-out" : "transform 0.3s ease-out",
    boxShadow: isHovered 
      ? `0 25px 50px -12px rgba(0, 0, 0, 0.4), 0 0 30px ${themeColors.glow}`
      : "0 10px 30px -10px rgba(0, 0, 0, 0.3)",
  };

  const sizeStyles = { compact: "w-40 sm:w-44", default: "w-48 sm:w-56", large: "w-56 sm:w-72" };
  const imageSize = { compact: "h-32", default: "h-40", large: "h-56" };

  return (
    <div
      ref={cardRef}
      className={`${sizeStyles[size]} cursor-pointer relative group`}
      style={{ transformStyle: "preserve-3d" }}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={() => navigate(`/product/${product.id}`)}
    >
      <Card 
        className="overflow-hidden bg-gradient-to-br from-slate-900 to-slate-800 border-slate-700/50 h-full"
        style={cardStyles}
      >
        <div className={`relative ${imageSize[size]} overflow-hidden bg-slate-800`}>
          <img
            src={product.images?.[0] || "/placeholder-product.png"}
            alt={product.title}
            className={`w-full h-full object-cover transition-transform duration-300 ${isHovered ? "scale-110" : "scale-100"}`}
          />
          <div 
            className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
            style={{
              background: `radial-gradient(circle at ${mousePosition.x * 100}% ${mousePosition.y * 100}%, ${themeColors.glow}, transparent 60%)`
            }}
          />
          
          {/* Badges - Top Left */}
          <div className="absolute top-2 left-2 flex flex-col gap-1">
            {product.is_featured && (
              <Badge className={`${themeColors.badge} text-xs`}>Featured</Badge>
            )}
            {discountPercent && discountPercent > 0 && (
              <Badge className="bg-red-500 text-white text-xs flex items-center gap-1">
                <Tag className="w-3 h-3" />-{discountPercent}%
              </Badge>
            )}
            {product.free_shipping && (
              <Badge className="bg-blue-500 text-white text-xs flex items-center gap-1">
                <Truck className="w-3 h-3" />Free Ship
              </Badge>
            )}
            {hasVariants && (
              <Badge className="bg-purple-500 text-white text-xs flex items-center gap-1">
                <Layers className="w-3 h-3" />{product.variants.length} Options
              </Badge>
            )}
          </div>
          
          {/* Quick Actions - Top Right */}
          <div className="absolute top-2 right-2 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <Button size="icon" variant="secondary" className="w-8 h-8 rounded-full bg-white/90 hover:bg-white shadow-lg" onClick={handleLike}>
              <Heart className={`w-4 h-4 ${isLiked ? "fill-red-500 text-red-500" : "text-slate-600"}`} />
            </Button>
            <Button size="icon" variant="secondary" className="w-8 h-8 rounded-full bg-white/90 hover:bg-white shadow-lg" onClick={(e) => { e.stopPropagation(); navigate(`/product/${product.id}`); }}>
              <Eye className="w-4 h-4 text-slate-600" />
            </Button>
          </div>

          {/* Rating Badge - Bottom Left */}
          {product.rating_average > 0 && (
            <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-black/70 rounded-full px-2 py-1">
              <Star className="w-3 h-3 fill-[#FFD300] text-[#FFD300]" />
              <span className="text-xs text-white font-medium">{product.rating_average.toFixed(1)}</span>
            </div>
          )}
        </div>
        
        <div className="p-3 space-y-2">
          {/* SKU Display */}
          {product.sku && (
            <div className="flex items-center gap-1 text-xs text-slate-500">
              <Package className="w-3 h-3" />
              <span className="font-mono">{product.sku}</span>
            </div>
          )}
          
          {showMerchant && product.business_name && (
            <div className="flex items-center gap-1 text-xs text-slate-400">
              <Store className="w-3 h-3" />
              <span className="truncate">{product.business_name}</span>
            </div>
          )}
          
          <h3 className="font-medium text-white text-sm line-clamp-2 min-h-[2.5rem]">{product.title}</h3>
          
          <div className="flex items-center justify-between pt-1">
            <div>
              {isOnSale && originalPrice ? (
                <>
                  <p className="text-lg font-bold" style={{ color: themeColors.accent }}>${product.price?.toLocaleString()}</p>
                  <p className="text-xs text-slate-500 line-through">${originalPrice?.toLocaleString()}</p>
                </>
              ) : hasVariants ? (
                <>
                  <p className="text-lg font-bold" style={{ color: themeColors.accent }}>
                    From ${Math.min(...product.variants.map(v => v.price || product.price)).toLocaleString()}
                  </p>
                  <p className="text-xs text-slate-500">{product.currency || "JMD"}</p>
                </>
              ) : (
                <>
                  <p className="text-lg font-bold" style={{ color: themeColors.accent }}>${product.price?.toLocaleString()}</p>
                  <p className="text-xs text-slate-500">{product.currency || "JMD"}</p>
                </>
              )}
            </div>
            <Button
              size="icon"
              className="w-9 h-9 rounded-full transition-all duration-300"
              style={{ 
                backgroundColor: addedSuccess ? "#22c55e" : themeColors.accent,
                color: ["gold", "yellow", "orange", "lime", "white", "teal"].includes(theme) ? "#000" : "#fff"
              }}
              onClick={handleAddToCart}
              disabled={isAdding}
            >
              {addedSuccess ? <Check className="w-4 h-4 animate-bounce" /> : isAdding ? <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" /> : <Plus className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </Card>
      <div 
        className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-3/4 h-4 rounded-full blur-lg transition-all duration-300"
        style={{
          background: isHovered ? themeColors.glow : "rgba(0,0,0,0.3)",
          transform: `translateX(-50%) scale(${isHovered ? 1.2 : 1})`
        }}
      />
    </div>
  );
}

export function ProductCardCompact({ product, onAddToCart }) {
  return <ProductCard3D product={product} size="compact" showMerchant={false} onAddToCart={onAddToCart} />;
}

export function ProductCardFeatured({ product, theme = "gold" }) {
  return <ProductCard3D product={product} size="large" theme={theme} />;
}
